import React, { useEffect, useRef, useState } from 'react';

import ActionSummaryEmptyState, { VARIANT_EMPTY, VARIANT_FILTERED } from './ActionSummaryEmptyState';
import ActionSummaryFilters from './ActionSummaryFilters';
import ActionSummaryTable from './ActionSummaryTable';

import Button from 'CommonComponents/Button';

export default function ActionSummaryIndex(props) {
    // `null` rather than `[]` means "no fetch has completed yet". It matters because isLoading starts
    // as `{}`, so `isLoading.actionSummary` is undefined (falsy) on the first render — with `[]` the
    // empty state would flash for a frame every time the tab is opened, before the fetch resolves.
    const [actionSummary, setActionSummary] = useState(null);
    const [isLoading, setIsLoading] = useState({});
    const [actionSummaryFilters, setActionSummaryFilters] = useState({});
    const [actionSummaryOrder, setActionSummaryOrder] = useState({ order_by: '', order: '' });
    // Bumped to remount ActionSummaryFilters, resetting its Formik state along with the data.
    const [filtersKey, setFiltersKey] = useState(0);
    // Set when a fetch fails, so the failure is distinguishable from a genuinely empty summary.
    const [hasError, setHasError] = useState(false);
    // Identifies the newest in-flight request; see the guards in fetchActionSummary.
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (props.isActive) {
            fetchActionSummary();
        }
    }, [props.isActive]);

    const fetchActionSummary = (action, options) => {
        let filters = {};

        if (action === 'filters') {
            setActionSummaryFilters(options);
            filters = options;
            filters.order_by = actionSummaryOrder.order_by;
            filters.order = actionSummaryOrder.order;
        } else if (action === 'sort') {
            filters = actionSummaryFilters;
            if (options !== actionSummaryOrder.order_by) {
                setActionSummaryOrder({ order_by: options, order: 'asc' });
                filters.order_by = options;
                filters.order = 'asc';
            } else {
                setActionSummaryOrder({ order_by: options, order: actionSummaryOrder.order === 'asc' ? 'desc' : 'asc' });
                filters.order_by = options;
                filters.order = actionSummaryOrder.order === 'asc' ? 'desc' : 'asc';
            }
        } else {
            setActionSummaryFilters({});
            filters.order_by = actionSummaryOrder.order_by;
            filters.order = actionSummaryOrder.order;
        }

        setHasError(false);
        loading('actionSummary', true);

        // Responses can land out of order — apply a slow owner filter, then hit Reset all quickly,
        // and the stale filtered response can resolve last. Letting it write state would show
        // "No actions yet" on an event that demonstrably has actions, so only the newest request is
        // allowed to touch state. There is no abort: the response is fetched and then discarded.
        const requestId = ++requestIdRef.current;
        const isCurrentRequest = () => requestId === requestIdRef.current;

        axios
            .get(props.actionSummaryUrl, { params: { ...filters } })
            .then(({ data }) => {
                if (isCurrentRequest()) {
                    setActionSummary(data.summary);
                }
            })
            .catch((e) => {
                console.error(e);

                if (isCurrentRequest()) {
                    setHasError(true);
                }
            })
            .finally(() => {
                if (isCurrentRequest()) {
                    loading('actionSummary', false);
                }
            });
    };

    const loading = (key, value = true) => {
        setIsLoading((pervLoading) => ({ ...pervLoading, [key]: value }));
    };

    // Matches what the filter modal's own "Reset all" does (handleReset + onReset): clear the form
    // and refetch unfiltered. Without the remount the Owner box would keep the typed value while
    // the data below it reset, which reads as the filter still being applied.
    const handleClearFilters = () => {
        setFiltersKey((prevKey) => prevKey + 1);
        fetchActionSummary();
    };

    const filtersActive = Boolean(actionSummaryFilters.owner?.trim());
    const hasCompletedFetch = actionSummary !== null;

    // A failed fetch falls back to the bare table frame — headers over an empty body — rather than
    // the empty state. After an error the app does not know whether this event has actions, and both
    // empty-state variants would assert that it doesn't.
    const showTable = hasError || !hasCompletedFetch || isLoading.actionSummary || actionSummary.length > 0;

    return (
        <div className="table__card">
            <div className="tw-flex tw-justify-between">
                <h4 className="table__header tw-mb-6">{$t('Actions Summary')}</h4>

                <div className="tw-flex tw-gap-x-1.5">
                    <Button variant="primary" as="a" href={props.actionSummaryExportUrl}>
                        {$t('Download')}
                    </Button>
                    {/* Download and Filter stay visible when the table is empty. Hiding Filter would
                        trap the user in the filtered-empty state with no way to clear the filter
                        that emptied the page. */}
                    <ActionSummaryFilters key={filtersKey} onSubmit={(values) => fetchActionSummary('filters', values)} onReset={() => fetchActionSummary()} />
                </div>
            </div>

            <div className="tw-space-y-8">
                {showTable ? (
                    // Rendered during a fetch as well as when there are rows, so the column headers
                    // stay put while loading instead of the card jumping between two layouts.
                    <ActionSummaryTable isLoading={!hasError && (!hasCompletedFetch || isLoading.actionSummary)} actionSummary={actionSummary ?? []} />
                ) : (
                    <ActionSummaryEmptyState
                        variant={filtersActive ? VARIANT_FILTERED : VARIANT_EMPTY}
                        onGoToProjectPlan={props.onGoToProjectPlan}
                        onClearFilters={handleClearFilters}
                    />
                )}
            </div>
        </div>
    );
}
